import { AuthPaths, RoleEnum, RouteEnum } from "../../../models/enums";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { AuthApiClient } from "../../../api/AuthApiClient";
import { AvailableApps } from "../../../types/apps";
import CircularLoading from "../../progress/CircularLoading";
import SelectApplicationsIcon from "../../icons/SelectApplicationsIcon";
import SelectedApplicationsIcon from "../../icons/SelectedApplicationsIcon";
import { getRolePath } from "../../../helpers/userLinkHelper";
import { useAuthenticatedUser } from "../../../hooks/useAuthenticatedUser";
import { useNavigate } from "react-router-dom";

interface AppOption {
  label: string;
  path: string;
  id: string | AvailableApps;
}

const APP_OPTIONS: AppOption[] = [
  {
    label: "Luminary",
    path: AuthPaths.Luminary,
    id: AvailableApps.luminary,
  },
  {
    label: "Sensory",
    path: RouteEnum.Home,
    id: AvailableApps.sensory,
  },
  {
    label: "Leak Finder",
    path: RouteEnum.LeakFinderClientUploads,
    id: AvailableApps.leakfinder,
  },
  {
    label: "EnerGPT Connected",
    path: AuthPaths.EnerGPT,
    id: AvailableApps.energpt,
  },
  {
    label: "ProveZero Registry",
    path: AuthPaths.Marketplace,
    id: AvailableApps.marketplace,
  },
  {
    label: "CI Intelligence",
    path: AuthPaths.CIintelligence,
    id: AvailableApps.luminary,
  },
];

const ApplicationSelect = (): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [options, setOptions] = useState<AppOption[]>([]);

  const { role } = useAuthenticatedUser();

  const authApiClient = new AuthApiClient();

  const navigate = useNavigate();

  useEffect(() => {
    async function setAvailabeApps() {
      const user = await authApiClient.getCurentUser();

      let availableOptions = APP_OPTIONS.filter(option =>
        user.tenant.available_apps.includes(option.id as AvailableApps)
      );

      if (role === RoleEnum.OPERATOR_READ_ONLY) {
        availableOptions = availableOptions.filter(
          option =>
            option.id !== AvailableApps.energpt &&
            option.id !== AvailableApps.marketplace
        );
      }

      setOptions(availableOptions);
    }

    setAvailabeApps();
  }, [role]);

  const isOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (path: string) => {
    setAnchorEl(null);

    const userMainLink = getRolePath(role as RoleEnum);

    if (path.startsWith(RouteEnum.Home)) {
      if (
        window.location.pathname === userMainLink &&
        path === RouteEnum.Home
      ) {
        return;
      }

      if (path === RouteEnum.Home && role) {
        navigate(userMainLink, { replace: true });

        return;
      }

      if (!role) {
        return navigate(RouteEnum.Unauthorized, { replace: true });
      }

      navigate(path, { replace: true });
    } else {
      window.location.href = path;
    }
  };

  return (
    <Box
      sx={{
        dislpay: "flex",
        borderRadius: "8px",
        alighItems: "center",
        justifyContent: "center",
      }}
    >
      <IconButton
        sx={{ p: 0 }}
        onClick={handleClick}
        aria-controls={isOpen ? "app-selector-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? "true" : undefined}
      >
        {isOpen ? <SelectedApplicationsIcon /> : <SelectApplicationsIcon />}
      </IconButton>

      <Menu
        id="app-selector-menu"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        MenuListProps={{ "aria-labelledby": "app-selector-button" }}
        sx={{
          ".MuiList-padding": {
            p: 0,
          },
          ".MuiPaper-root": {
            borderRadius: "0.5em",
            boxShadow: "none",
            border: theme =>
              `1px solid ${theme.palette.otherOutlineBorder.main}`,
          },
        }}
      >
        <Typography
          variant="body2Bold"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            height: "36px",
            width: "100%",
            pl: 2,
            py: 0,
            marginBottom: "4px",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          Available products
        </Typography>

        {options.length ? (
          options.map(({ label, path }) => (
            <MenuItem
              key={path}
              onClick={() => handleMenuItemClick(path)}
              sx={{
                height: "36px",
                "&:hover": {
                  backgroundColor: theme =>
                    theme.palette.primary.shades?.["8p"],
                  "&::before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    backgroundColor: theme => theme.palette.primary.main,
                  },
                },
              }}
            >
              <Typography variant="body2Regular">{label}</Typography>
            </MenuItem>
          ))
        ) : (
          <CircularLoading />
        )}

        <MenuItem
          key={"Administration"}
          onClick={() => handleMenuItemClick(AuthPaths.Administration)}
          sx={{
            height: "36px",
            marginTop: "4px",
            pl: 2,
            py: 0,
            borderTop: 1,
            borderColor: "divider",
            "&:hover": {
              backgroundColor: theme => theme.palette.primary.shades?.["8p"],
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                backgroundColor: theme => theme.palette.primary.main,
              },
            },
          }}
        >
          <Typography variant="body2Regular">{"Administration"}</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ApplicationSelect;
